import styled from 'styled-components';
import { colors, colors2 } from 'oolib';
import { transition } from 'oolib/dist/themes/mixins';

export const StyledCard = styled.div`
  border:none;
  background: #F7F7F7;
  border-radius: 6px;
`;

export const StyledInputHeader = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 53px;
  background: ${({ $active }) => $active ? colors2.white : '#F7F7F7'};
  box-shadow: ${({ $active }) => $active ? '0 0.4rem 1rem 0 rgba(0, 0, 0, 0.12)' : 'none'};
  cursor: ${({ $clickable, $disabled }) => $disabled ? 'not-allowed' : $clickable ? 'pointer' : 'default'};
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  border-radius: 6px;
  border: none;
  outline: ${({ $active }) => $active ? `1px solid ${colors2.grey80}` : 'none'};
  outline-offset: -1px;
  ${transition("box-shadow")}
  ${({ $active, $hasModifier }) => $active && $hasModifier && `
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  `}
  ${({ $disabled, $active }) => !$disabled && `
  &:hover {
    outline: 1px solid ${$active ? colors2.grey80 : '#C6C6C6'};
  }
  `}

  &:focus-within {
    background: ${colors2.white};
    outline: 1px solid ${colors2.grey80};
    box-shadow: 0 0.4rem 1rem 0 rgba(0, 0, 0, 0.12);
  }
`;

export const StyledSlotIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem 0.5rem 0.6rem;
  cursor: grab;
  color: ${colors2.grey80};
  ${transition("color", "background")}
  height: 100%;

  &:hover {
    background: ${colors2.white};
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
`;

export const StyledFieldSlot = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: ${({ $isFirst }) => $isFirst ? '1rem' : '1rem 1rem 1rem 0'};
  ${transition("background")}
  height:100%;
  cursor: ${({ $clickable, $disabled }) => $disabled ? 'not-allowed' : $clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${colors2.white};
    ${({ $isFirst }) => $isFirst && `
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    `}
    ${({ $isLast }) => $isLast && `
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    `}
  }
`;

export const StyledFieldName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  // fade effect
  mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
`;

export const StyledFieldContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: ${({ $isPlaceholder }) => $isPlaceholder ? 'center' : 'stretch'};
  ${({ $isPlaceholder }) => $isPlaceholder ? 'gap: 0.35rem;' : ''}
  flex-direction: ${({ $isPlaceholder }) => $isPlaceholder ? 'row' : 'column'};
  ${({ $hasIndicator }) => $hasIndicator && `
    border-left: 1px solid #DFDFDF;
    padding-left: 1rem;
  `}
`;

export const StyledTrailingIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${colors2.grey30};
  ${transition("color")}

  ${StyledInputHeader}:hover & {
    color: ${colors2.grey80};
  }
`;

export const StyledActionSlot = styled.div`
  display: flex;
  align-items: center;
  color: ${colors2.grey30};

  ${StyledInputHeader}:hover & {
    color: ${colors2.grey80};
  }
`;

export const StyledSideAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${transition("color", "background")}
  min-width: 34px;

  &:hover {
    ${({ $hoverColor }) => $hoverColor === 'red'
    ? `color: ${colors2.red} !important; background: ${colors2.redBG};`
    : `background: ${colors2.white};`}
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

export const StyledModifierSection = styled.div`
  border: 1px solid ${({ $active }) => $active ? colors2.grey80 : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 4px 10px 0 rgba(0, 0, 0, 0.12)' : 'none'};
  cursor: ${({ $disabled, $loading }) => $disabled ? 'default' : $loading ? 'not-allowed' : 'pointer'};
  background: ${({ $active }) => $active ? colors2.white : '#EFEFEF'};
  ${transition("border-color", "background")}
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  /* $flushRadius (opt-in): the row runs edge to edge inside a bordered card, so
     it squares off against the card's sides and carries the card's bottom
     radius — except when its lightbox is open, where it lifts off the surface
     and rounds fully, as it does everywhere else. */
  border-radius: ${({ $active, $standalone, $flushRadius }) => $flushRadius
    ? ($active ? '6px' : `0 0 ${$flushRadius} ${$flushRadius}`)
    : ($active || $standalone) ? '6px' : '0 0 6px 6px'};
  ${({ $disabled, $active }) => !$disabled && `
  &:hover {
    background: ${colors2.white};
    border-radius: 6px;
    border-color: ${$active ? colors2.grey80 : '#C6C6C6'};
    }
  `}
`;

export const StyledModifierCaret = styled.div`
  display: flex;
  ${transition("color")}
  color: ${colors2.grey40};
`;

export const StyledModifierLabel = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  // fage effect
  mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
`;

export const StyledModifierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 1rem;
  min-height: 28px;
  color: ${colors2.grey80};

  &:hover ${StyledModifierCaret} {
    color: ${colors2.grey80};
  }
`;

// Forces tooltip wrapper divs to stretch without breaking layout
const tooltipPassthrough = `
  display: flex;
  align-self: stretch;
  & > * {
    display: flex !important;
  }
  & > * > * {
    display: flex !important;
  }
`;

export const StyledTooltipSlot = styled.div`
  ${tooltipPassthrough}
  flex: 1;
  min-width: 0;
  & > * {
    flex: 1;
    min-width: 0;
  }
  & > * > * {
    flex: 1;
    min-width: 0;
  }
`;

export const StyledTooltipPassthrough = styled.div`
  ${tooltipPassthrough}
`;

// ─── Stepper Mode ───

export const StyledStepperButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  border: none;
  background: none;
  font-family: inherit;
  cursor: pointer;
  color: ${colors2.grey20};
  ${transition("color", "background")}
  flex-shrink: 0;

  &:first-child { border-radius: 6px 0 0 6px; }
  &:last-child { border-radius: 0 6px 6px 0; }

  &:hover {
    background: ${colors2.white};
    color: ${colors2.grey80};
  }

  ${StyledInputHeader}:hover & {
    color: ${colors2.grey80};
  }

  ${StyledInputHeader}:focus-within & {
    color: ${colors2.grey80};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const StyledStepperCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 0;
  cursor: text;
`;

export const StyledStepperValueInput = styled.input`
  font-family: 'DM Sans', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${colors2.black};
  background: none;
  border: none;
  outline: none;
  text-align: center;
  width: 56px;
  padding: 0;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
